import {
  normalizeScan,
  VOID_LABEL_TEXT,
  type AsIsOrder,
  type LabelSkip,
  type LabelsPrintedHere,
  type MarketplaceChannel,
  type PickingRouteTask,
  type PickRoute,
  type RouteLeftOrder,
  type WarehouseMap as WarehouseMapData,
} from '@wms/domain';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { api, apiBlob, ApiError, errorMessage, type FloorAction } from '../api';
import { feedback } from '../feedback';
import { fmt } from '../format';
import { isOffline, type Stale } from '../loading';
import { printPdf, printZpl } from '../printing';
import { navigate } from '../router';
import {
  deskDownText,
  labelCount,
  labelMissing,
  labelsAhead,
  labelsCard,
  printableHere,
  printedHereDone,
  printedHereText,
  printLabelsHere,
  PRINTED_HERE_QUESTION,
  type HerePrinter,
} from '../routeLabels';
import { useWedgeScanner } from '../scan';
import { store, useSession } from '../session';
import { ChannelBadge, ErrorBanner, Modal, ProductThumb, SCAN_WORDS, ScanField, SellerChip, Spinner, useFloorAction, useLoad, useStaleReport, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { RouteMap } from '../ui/map/RouteMap';
import { NextStep, type StepLink } from '../ui/NextStep';
import { LabelStateCell, printedAt, printsChanged, usePrintStates } from './DeskPrinter';
import { CancelRouteDialog, PutBackList } from './PickRoutes';

const MAP_KEY = 'wms.pickMap';
/** the route changes under the picker (an order cancelled, a stop taken on another phone) */
const REFRESH_MS = 10_000;
/** answers after which the screen fetches the route again */
const STALE = new Set(['STOP_DONE', 'ROUTE_CLOSED', 'NOT_FOUND', 'STOP_CHANGED']);
/** a handheld below this width: the map folds away until asked for (beside the stop from 1100 px) */
const WIDE = '(min-width: 900px)';

const SHORT_REASONS = ['Göz boş', 'Gözde eksik adet var', 'Ürün hasarlı', 'Gözde başka ürün var'];

/** a cart's tote: "Sepet 4"; a single-order route's cart is one tote */
const tote = (slot: number | null) => (slot === null ? 'Sepet' : `Sepet ${slot}`);

/** "Durak 3": the stop's place on the route as it stands (a cancelled order takes its stops away) */
const place = (route: PickRoute, sequence: number) => route.tasks.findIndex((t) => t.sequence === sequence) + 1;

/** The marketplace of each order of the route, by order number: its stops, the orders that left it, the own-box ones. */
function channelsOf(route: PickRoute): Map<string, MarketplaceChannel> {
  const channels = new Map<string, MarketplaceChannel>();
  for (const t of route.tasks) for (const d of t.distribution) channels.set(d.orderNumber, d.channel);
  for (const o of route.leftOrders) channels.set(o.orderNumber, o.channel);
  for (const o of route.asIs) channels.set(o.orderNumber, o.channel);
  return channels;
}

function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => window.matchMedia?.(query).matches ?? true);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    const on = () => setMatch(list.matches);
    list.addEventListener('change', on);
    return () => list.removeEventListener('change', on);
  }, [query]);
  return match;
}

/**
 * The picker's screen: one stop at a time, big enough to read from the cart, laid out for a
 * phone held in one hand. Scan the bin label, then the product; the server checks both. A bin
 * that holds less is reported short. The route is fetched again every few seconds and when the
 * phone wakes up: orders get cancelled while the picker walks.
 */
export function PickWalk({ routeId }: { routeId: string }) {
  const toast = useToast();
  const { isSupervisor } = useSession();
  const [route, setRoute] = useState<PickRoute | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // a refresh failed while a route is on screen: the strip says how old it is (an order may be cancelled meanwhile)
  const [stale, setStale] = useState<Stale | null>(null);
  const loadedAt = useRef(0);
  const [binOk, setBinOk] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [flash, setFlash] = useState<'' | 'flash-ok' | 'flash-bad'>('');
  // the stop as it was when "Eksik bildir" was tapped: a refresh meanwhile must not swap the dialog onto the next stop
  const [shortTask, setShortTask] = useState<PickingRouteTask | null>(null);
  const shortOpen = shortTask !== null;
  const wide = useMedia(WIDE);
  const [wideMap, setWideMap] = useState(() => store.get(MAP_KEY) !== 'closed');
  const [narrowMap, setNarrowMap] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // what the screen shows; every answer to an action bumps the version, so a refresh sent before it is dropped
  const shown = useRef<PickRoute | null>(null);
  const version = useRef(0);
  const busy = useRef(false);
  // a reader's scan outside the field and the stop's buttons: a lost answer is sent again with its key
  const walk = useFloorAction();
  // a product scan whose answer was lost: "geri al" would take back the stop before it
  const [scanHeld, setScanHeld] = useState(false);

  // a cancelled route has nothing left to walk, even with stops never visited
  const task = route?.status === 'cancelled' ? null : (route?.tasks.find((t) => t.status === 'pending') ?? null);
  const step: 'bin' | 'product' = task && binOk === task.sequence ? 'product' : 'bin';

  const signal = (tone: 'ok' | 'bad', text: string, sound: 'ok' | 'error' | 'done' = tone === 'ok' ? 'ok' : 'error') => {
    feedback(sound);
    setMessage({ tone, text });
    setFlash('');
    requestAnimationFrame(() => setFlash(tone === 'ok' ? 'flash-ok' : 'flash-bad'));
  };

  const show = (next: PickRoute) => {
    version.current += 1;
    shown.current = next;
    loadedAt.current = Date.now();
    setStale(null);
    setRoute(next);
  };

  /** Tells the picker what changed under them since the last look. */
  const notice = (before: PickRoute, next: PickRoute) => {
    if (next.status === 'cancelled' && before.status !== 'cancelled') return signal('bad', 'Rota iptal edildi.');
    // a cancelled order, or one held because its marketplace no longer knows it: its tote goes to the return shelf alike
    const leaves = (o: PickRoute['leftOrders'][number]) => o.status === 'cancelled' || (o.status === 'on_hold' && o.channelUnknown === true);
    const gone = next.leftOrders.filter((o) => leaves(o) && !before.leftOrders.some((b) => b.orderId === o.orderId && b.status === o.status && leaves(b)));
    const said = (status: string, words: string) => {
      const numbers = gone.filter((o) => o.status === status).map((o) => o.orderNumber);
      return numbers.length > 0 ? `${numbers.join(', ')} ${words}` : null;
    };
    const why = gone.length > 0 ? [said('cancelled', 'iptal edildi'), said('on_hold', 'pazaryerinde bulunamadı')].filter(Boolean).join('; ') : null;
    const was = before.status === 'cancelled' ? undefined : before.tasks.find((t) => t.status === 'pending');
    if (was) {
      const now = next.tasks.find((t) => t.sequence === was.sequence);
      const then = next.tasks.some((t) => t.status === 'pending') ? 'Sıradaki durağa geçin.' : 'Toplanacak durak kalmadı.';
      if (!now) return signal('bad', `Durak ${place(before, was.sequence)} rotadan çıktı${why ? ` (${why})` : ''}. ${then}`);
      if (now.status === 'pending' && now.quantity !== was.quantity) return signal('bad', `Bu durak değişti: ${why ?? 'rota güncellendi'}, artık ${now.quantity} adet alın`);
      // done elsewhere (another phone): what the screen said about this stop no longer holds
      if (now.status !== 'pending' && !why) return setMessage(null);
    }
    // a red strip shows only for a tote that holds something
    if (why) signal('bad', gone.some((o) => o.units > 0) ? `${why}: kırmızı uyarıdaki sepeti iade rafına boşaltın.` : `${why}; rota güncellendi.`);
  };

  // quiet: after an answer that already said what changed
  const refresh = async (quiet = false) => {
    const mine = version.current;
    try {
      const next = await api<PickRoute>('GET', `/api/wms/pick-routes/${routeId}`);
      if (mine !== version.current || busy.current) return;
      const before = shown.current;
      shown.current = next;
      loadedAt.current = Date.now();
      setRoute(next);
      setLoadError(null);
      setStale(null);
      if (before && !quiet) notice(before, next);
    } catch (e) {
      if (mine !== version.current) return;
      if (!shown.current) setLoadError(errorMessage(e));
      else setStale({ error: errorMessage(e), offline: isOffline(e), since: loadedAt.current });
    }
  };
  const latest = useRef(refresh);
  latest.current = refresh;
  const retry = useCallback(() => latest.current(), []);
  useStaleReport(stale, retry);

  useEffect(() => {
    shown.current = null;
    setRoute(null);
    setLoadError(null);
    setStale(null);
    void latest.current();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void latest.current();
    }, REFRESH_MS);
    // a phone taken out of the pocket shows where the route stands at once
    const wake = () => {
      if (document.visibilityState === 'visible') void latest.current();
    };
    document.addEventListener('visibilitychange', wake);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [routeId]);

  /** Runs an action against the route; a refusal that means the screen is behind fetches it again. */
  const act = async (run: () => Promise<void>, action?: FloorAction) => {
    // a second scan or tap while the first is on its way: the first answer decides
    // (a lost one being sent again stays lost: it goes again after this answer)
    if (busy.current) {
      if (action && action.tries > 0) action.outcome = 'lost';
      return;
    }
    let stale = false;
    busy.current = true;
    try {
      await run();
    } catch (e) {
      signal('bad', errorMessage(e));
      // NOT_RECEIVED: a "geri al" sent again never reached the server and was not done
      stale = !(e instanceof ApiError) || STALE.has(e.code) || e.code === 'SHORT_NOT_UNDOABLE' || e.code === 'NOTHING_TO_UNDO' || e.code === 'NOT_RECEIVED';
    } finally {
      busy.current = false;
    }
    if (stale) await refresh(true);
  };

  const confirm = (stop: PickingRouteTask, productBarcode: string | null, action?: FloorAction) =>
    act(async () => {
      if (route?.status === 'open') await api('POST', `/api/wms/pick-routes/${routeId}/start`, undefined, { action });
      // the quantity on screen: the server refuses it when the stop changed meanwhile
      const next = await api<PickRoute>(
        'POST',
        `/api/wms/pick-routes/${routeId}/stops/${stop.sequence}/confirm`,
        {
          locationBarcode: stop.location.barcode,
          productBarcode,
          quantity: stop.quantity,
        },
        { action },
      );
      show(next);
      setBinOk(null);
      // what the server booked, not what the screen asked for
      const counted = next.tasks.find((t) => t.sequence === stop.sequence)?.pickedQty ?? stop.quantity;
      const text = `${counted} adet ${stop.product.sku} alındı`;
      if (next.status === 'completed') signal('ok', `${text}. Rota tamamlandı.`, 'done');
      else signal('ok', text);
    }, action);

  // geri al: the latest confirmed stop of this route becomes the next stop again
  const undo = (action?: FloorAction) =>
    act(async () => {
      const { route: next, undoneSequence } = await api<{ route: PickRoute; undoneSequence: number }>('POST', '/api/wms/picking/undo-last-pick', { routeId }, { action });
      show(next);
      setBinOk(null);
      const stop = next.tasks.find((t) => t.sequence === undoneSequence);
      signal('ok', `Durak ${place(next, undoneSequence)} geri alındı${stop ? ` (${stop.location.code}, ${stop.quantity} adet ${stop.product.sku})` : ''}. Ürünleri doğru sepete koyup durağı yeniden okutun.`);
    }, action);

  // eksik bildirimini geri al: the latest short stop is walked again, its bin not counted yet
  const undoShort = (action?: FloorAction) =>
    act(async () => {
      const { route: next, undoneSequence } = await api<{ route: PickRoute; undoneSequence: number }>('POST', '/api/wms/picking/undo-short', { routeId }, { action });
      show(next);
      setBinOk(null);
      const stop = next.tasks.find((t) => t.sequence === undoneSequence);
      signal('ok', `Eksik bildirimi geri alındı. Durak ${place(next, undoneSequence)}${stop ? ` (${stop.location.code})` : ''} yeniden açıldı: gözü okutup ürünleri alın.`);
      toast('Siparişler yeniden toplamada, göz sayımı iptal edildi', 'info');
    }, action);

  const onScan = async (code: string, action?: FloorAction) => {
    if (!task) return;
    if (step === 'bin') {
      if (code === task.location.barcode || code === task.location.code.toUpperCase()) {
        setBinOk(task.sequence);
        signal('ok', `Göz doğru: ${task.location.code}. Şimdi ürünü okutun.`);
      } else {
        signal('bad', `Yanlış göz okuttunuz. ${task.location.code} gözüne gidin.`);
      }
      return;
    }
    // the server resolves EAN, ITF-14 case barcodes and SKUs within the seller's catalog
    if (code === task.location.barcode) return signal('ok', 'Göz zaten okutuldu. Ürünü okutun.');
    await confirm(task, normalizeScan(code), action);
  };
  const onWedge = async (code: string) => {
    if (!(await walk.run(SCAN_WORDS, (action) => onScan(code, action)))) signal('bad', 'Önceki okutma henüz bitmedi; bekleyip tekrar okutun.');
  };
  useWedgeScanner(task && !shortOpen && !cancelOpen ? (code) => void onWedge(code) : null);

  // hash routing keeps the page where the list it came from was scrolled: the walk opens at its top (the
  // labels card, then the stop); the scan field moves it only as far as the field needs (reveal="nearest")
  const opened = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!route || opened.current === routeId) return;
    opened.current = routeId;
    window.scrollTo(0, 0);
  }, [route, routeId]);

  if (loadError && !route) return <ErrorBanner message={loadError} onRetry={() => void refresh()} />;
  if (!route) return <Spinner />;

  const done = route.tasks.filter((t) => t.status !== 'pending').length;
  const total = route.tasks.length;
  const batch = route.mode === 'batch';
  // a single-order route names no order on its stops: its marketplace stands in the head (a batch's totes each show theirs)
  const single = batch ? undefined : channelsOf(route).values().next().value;
  // totes of orders cancelled on the way (or held because their marketplace no longer knows them) that hold something to take out
  const emptied = route.status === 'cancelled' ? [] : route.leftOrders.filter((o) => (o.status === 'cancelled' || o.channelUnknown === true) && o.units > 0);
  // the bin is looked at before a short is reported; a supervisor may report it from afar
  const shortLocked = step !== 'product' && !isSupervisor;
  const mapOpen = wide ? wideMap : narrowMap;
  // every unit of the stop is labelled at the bin: the label card under the product names their orders, no tote to show
  const allLabelled = task !== null && task.product.labelBeforePick && task.distribution.every((d) => labelledAtBin(route, d.orderId));
  const banner = message && <div className={`banner ${message.tone} walk-msg`}>{message.text}</div>;

  return (
    <>
      <div className="walk-top">
        <div className="walk-head">
          <a className="btn ghost" href="#/toplama" aria-label="Rotalara dön">
            <Icon name="back" />
            <span className="walk-wide">Rotalar</span>
          </a>
          <b className="walk-count">{task ? `Durak ${place(route, task.sequence)} / ${total}` : `${done} / ${total} durak`}</b>
          {single && <ChannelBadge channel={single} short />}
          <span className="walk-meta walk-wide">
            <span className="chip">{batch ? 'Toplu toplama' : 'Tekli toplama'}</span>
            <span className="chip">{route.orderIds.length} sipariş</span>
            {route.totalDistanceM !== null && <span className="chip">{fmt.dec(route.totalDistanceM)} m yürüyüş</span>}
          </span>
          <span className="spacer" />
          {(route.status === 'open' || route.status === 'in_progress') && (
            <button className="btn sm ghost" onClick={() => setCancelOpen(true)} title="Rotayı bırakır; yeni siparişle birlikte yeniden rota oluşturabilirsiniz">
              <span className="walk-wide">Rotayı iptal et</span>
              <span className="walk-narrow">İptal</span>
            </button>
          )}
        </div>
        <div className="progress" aria-label="İlerleme">
          <i style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
        </div>
      </div>
      {cancelOpen && (
        <CancelRouteDialog
          routeId={routeId}
          picked={done}
          labelled={route.asIs.filter((o) => o.labelPrintedAt && !o.packed).reduce((n, o) => n + o.parcels, 0)}
          onClose={() => setCancelOpen(false)}
          onCancelled={() => {
            setCancelOpen(false);
            toast('Rota iptal edildi', 'ok');
            navigate('/toplama');
          }}
        />
      )}

      {task && emptied.length > 0 && <CancelledStrip orders={emptied} />}
      {!task && banner}
      {!task && walk.banner}

      <div className={`walk-layout${mapOpen ? ' with-map' : ''}`}>
        <div className="walk-stop">
          {/* right above the stop: in view with its scan field, beside the map on a wide screen */}
          {task && <LabelsAhead route={route} onPrinted={show} />}
          {task ? (
            <section className={`card stop-card ${flash}`} onAnimationEnd={() => setFlash('')}>
              <div className="card-b stack walk-card">
                <div className="walk-bin">
                  <div className="loc-big walk-loc" style={{ '--chars': task.location.code.length } as CSSProperties}>
                    {task.location.code}
                  </div>
                  {task.legDistanceM !== null && <span className="muted walk-wide">{fmt.dec(task.legDistanceM)} m</span>}
                </div>
                <div className="loc-parts">
                  <span>
                    Koridor <b>{task.location.aisle}</b>
                  </span>
                  <span>
                    Raf <b>{String(task.location.rack).padStart(2, '0')}</b>
                  </span>
                  <span>
                    Kat <b>{task.location.level}</b>
                  </span>
                  <span>
                    Göz <b>{task.location.bin}</b>
                  </span>
                </div>

                <div className="walk-what">
                  <ProductThumb name={task.product.name} imageUrl={task.product.imageUrl} />
                  <div style={{ minWidth: 0 }}>
                    <div className="row" style={{ gap: 6 }}>
                      <SellerChip code={task.product.tenantCode} />
                      <span className="mono muted">{task.product.sku}</span>
                    </div>
                    <div className="name">{task.product.name}</div>
                    {(task.lotNo || task.expiryDate) && (
                      <div className="row" style={{ gap: 6, marginTop: 4 }}>
                        {task.lotNo && <span className="chip">Lot {task.lotNo}</span>}
                        {task.expiryDate && (
                          <span className={`chip ${fmt.daysLeft(task.expiryDate) < 90 ? 'warn' : ''}`}>SKT {fmt.date(task.expiryDate)}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="walk-qty">
                    <div className="qty-big">{task.quantity}</div>
                    <div className="muted nowrap">adet al</div>
                  </div>
                </div>

                {batch && !allLabelled && (
                  <div className="slots" aria-label="Sepetlere dağıt">
                    {task.distribution.map((d) =>
                      labelledAtBin(route, d.orderId) ? (
                        <div key={d.allocationId} className="slot labelled">
                          {/* the badge on the top line: the tile stays as wide as its order number (two fit a 360px phone) */}
                          <small>
                            Etiketle <ChannelBadge channel={d.channel} short />
                          </small>
                          <b>
                            <Icon name="tag" size={18} />
                          </b>
                          <span>
                            × {d.quantity} · {d.orderNumber}
                          </span>
                        </div>
                      ) : (
                        <div key={d.allocationId} className="slot">
                          <small>
                            Sepet <ChannelBadge channel={d.channel} short />
                          </small>
                          <b>{d.toteSlot}</b>
                          <span>
                            × {d.quantity} · {d.orderNumber}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {task.product.labelBeforePick && <LabelAtBin task={task} route={route} onChanged={show} />}
                <VoidLabel route={route} orderIds={task.distribution.map((d) => d.orderId)} />

                <div className="steps walk-wide">
                  <div className={`step ${step === 'bin' ? 'now' : 'done'}`}>1 · Göz etiketini okut</div>
                  <div className={`step ${step === 'product' ? 'now' : ''}`}>2 · Ürün barkodunu okut</div>
                </div>
                <ScanField
                  placeholder={step === 'bin' ? `${task.location.code} etiketini okutun` : 'Ürün barkodunu okutun'}
                  onScan={onScan}
                  suggest={step === 'bin' ? { kinds: ['bin'] } : { kinds: ['product'], tenantId: task.product.tenantId }}
                  onHeld={setScanHeld}
                  disabled={shortOpen}
                  reveal="nearest"
                  // the labels card wins on every screen when the two do not fit: what to do before walking is
                  // read first, and a reader types into the field below it all the same
                  revealAbove={() => document.querySelector('.walk-stop > .labels-ahead')}
                  // the next stop (a taller one, or the labels card gone) brings the field back into view
                  revealKey={task.sequence}
                />
                {banner}
                {walk.banner}
                <div className="walk-actions">
                  <button
                    className={`btn${shortLocked ? ' locked' : ''}`}
                    aria-disabled={shortLocked}
                    disabled={walk.waiting}
                    onClick={() => (shortLocked ? signal('bad', `Önce ${task.location.code} gözünü okutun: eksik, göze bakınca bildirilir.`) : setShortTask(task))}
                  >
                    <Icon name="alert" /> Eksik bildir
                  </button>
                  {route.undoable && (
                    <button
                      className="btn"
                      onClick={() => void walk.run('Son işlemi geri al', undo)}
                      disabled={walk.waiting || scanHeld}
                      title={scanHeld ? 'Önce bağlantısı kopan okutmanın sonucu belli olmalı' : 'Son onaylanan durağı geri alır'}
                    >
                      <Icon name="undo" /> Son işlemi geri al
                    </button>
                  )}
                  {route.undoableShort !== null && (
                    <button
                      className="btn"
                      onClick={() => void walk.run('Eksik bildirimini geri al', undoShort)}
                      disabled={walk.waiting}
                      title={`Durak ${place(route, route.undoableShort)} için yapılan eksik bildirimini geri alır`}
                    >
                      <Icon name="undo" /> Eksik bildirimini geri al
                    </button>
                  )}
                  {isSupervisor && (
                    <button
                      className="btn ghost"
                      onClick={() => void walk.run('Okutmadan onayla', (action) => confirm(task, null, action))}
                      disabled={walk.waiting}
                      title="Gözü ve ürünü okutmadan bu durağı onaylar"
                    >
                      <Icon name="check" /> Okutmadan onayla
                    </button>
                  )}
                </div>
              </div>
            </section>
          ) : route.status === 'cancelled' ? (
            <section className="card">
              <div className="card-b stack">
                <div className="banner warn">
                  <Icon name="alert" />
                  <div>
                    <div className="big">Rota iptal edildi</div>
                    {route.putBack.length > 0
                      ? 'Yarım kalan siparişler toplamaya hazır listesine döndü. Onlar için aldığınız ürünleri gözlerine geri koyun:'
                      : 'Tamamı toplanan siparişlerin ürünlerini paketleme masasına götürün.'}
                  </div>
                </div>
                {route.putBack.length > 0 && <PutBackList items={route.putBack} />}
                <button className="btn primary big" onClick={() => navigate('/toplama')}>
                  Rotalara dön
                </button>
              </div>
            </section>
          ) : (
            <Finished
              route={route}
              onUndo={route.undoable && !walk.waiting ? () => void walk.run('Son işlemi geri al', undo) : null}
              onUndoShort={route.undoableShort !== null && !walk.waiting ? () => void walk.run('Eksik bildirimini geri al', undoShort) : null}
            />
          )}
        </div>

        <MapPanel
          route={route}
          open={mapOpen}
          onToggle={() => {
            if (!wide) return setNarrowMap(!narrowMap);
            store.set(MAP_KEY, wideMap ? 'closed' : 'open');
            setWideMap(!wideMap);
          }}
        />

        <section className="card stop-list walk-list">
          <div className="card-h">
            <h2>Duraklar</h2>
            <span className="sub">Yürüme sırasına göre</span>
          </div>
          <div className="table-wrap">
            <table className="t">
              <tbody>
                {route.tasks.map((t, i) => (
                  <tr key={t.sequence} className={t === task ? 'current' : t.status === 'picked' ? 'done' : t.status === 'short' ? 'short' : ''}>
                    <td className="num">{i + 1}</td>
                    <td className="mono">{t.location.code}</td>
                    <td className="hide-sm">
                      <SellerChip code={t.product.tenantCode} />
                    </td>
                    <td>{t.product.name}</td>
                    <td className="num">{t.quantity}</td>
                    <td>{t.status === 'picked' ? <Icon name="check" /> : t.status === 'short' ? 'Eksik' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {shortTask && (
        <ShortDialog
          routeId={routeId}
          task={shortTask}
          onClose={() => setShortTask(null)}
          onDone={(next, found) => {
            setShortTask(null);
            show(next);
            setBinOk(null);
            signal(
              'bad',
              `Eksik kaydedildi${found > 0 ? ` (${found} adet göze geri bırakıldı)` : ''}. Siparişler sorunlu listesine alındı, göz sayıma kilitlendi. Yanlışlıkla bildirdiyseniz “Eksik bildirimini geri al”.`,
            );
            toast('Eksik toplama kaydedildi', 'info');
          }}
          onStale={(text) => {
            setShortTask(null);
            signal('bad', text);
            void refresh(true);
          }}
        />
      )}
    </>
  );
}

/** The route on the warehouse floor plan; on a phone it waits folded until asked for. */
function MapPanel({ route, open, onToggle }: { route: PickRoute; open: boolean; onToggle: () => void }) {
  const map = useLoad(
    () => (open ? api<WarehouseMapData>('GET', `/api/wms/map?warehouseId=${route.warehouseId}`) : Promise.resolve(undefined)),
    [route.warehouseId, open],
  );
  const remaining = route.tasks.filter((t) => t.status === 'pending').length;
  return (
    <section className="card map-card walk-map">
      <div className="card-h">
        <Icon name="route" />
        <h2>Depo haritası</h2>
        <span className="sub">{remaining > 0 ? `${remaining} durak kaldı` : 'Rota tamamlandı'}</span>
        <span className="spacer" />
        <button className="btn sm walk-map-toggle" onClick={onToggle} aria-expanded={open}>
          {open ? 'Gizle' : 'Haritayı göster'}
        </button>
      </div>
      {open && (map.error ? <ErrorBanner message={map.error} onRetry={() => void map.reload()} /> : map.data ? <RouteMap map={map.data} route={route} /> : <Spinner />)}
    </section>
  );
}

/**
 * Eksik bildir: the picker counts what the bin holds (nothing is suggested), puts it back and
 * says why. The units found stay in the bin for the count; the orders wait for a supervisor.
 */
function ShortDialog(props: {
  routeId: string;
  task: PickingRouteTask;
  onClose: () => void;
  onDone: (route: PickRoute, found: number) => void;
  onStale: (message: string) => void;
}) {
  const { routeId, task, onClose, onDone, onStale } = props;
  const most = task.quantity - 1;
  const [found, setFound] = useState<number | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const short = useFloorAction();
  // until a reason is tapped, it follows the count
  const reason = picked ?? (found === null ? null : found === 0 ? 'Göz boş' : 'Gözde eksik adet var');
  const save = found !== null && found > 0 ? 'Ürünleri göze bıraktım, eksik kaydet' : 'Eksik olarak kaydet';
  const submit = async (action: FloorAction) => {
    if (found === null || reason === null) return;
    setBusy(true);
    try {
      const next = await api<PickRoute>(
        'POST',
        `/api/wms/pick-routes/${routeId}/stops/${task.sequence}/short`,
        { foundQty: found, reason, expectedQty: task.quantity },
        { action },
      );
      onDone(next, found);
    } catch (e) {
      if (e instanceof ApiError && STALE.has(e.code)) return onStale(errorMessage(e));
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Eksik bildir · ${task.location.code}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn walk-tap" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn danger walk-tap" disabled={busy || found === null || short.waiting} onClick={() => void short.run(save, submit)}>
            {save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        {short.banner}
        <p style={{ margin: 0 }}>
          Bu gözden <b>{task.quantity}</b> adet <b>{task.product.name}</b> alınacaktı. Gözde kaç adet buldunuz?
        </p>
        <div className="short-counter">
          <button className="btn" aria-label="Bir eksik" disabled={found === 0} onClick={() => setFound(found === null ? 0 : Math.max(0, found - 1))}>
            −
          </button>
          <output className={`short-count${found === null ? ' unset' : ''}`} aria-live="polite">
            {found ?? '?'}
          </output>
          <button className="btn" aria-label="Bir fazla" disabled={found !== null && found >= most} onClick={() => setFound(found === null ? Math.min(1, most) : Math.min(most, found + 1))}>
            +
          </button>
        </div>
        <button className={`chip select walk-tap${found === 0 ? ' on' : ''}`} style={{ alignSelf: 'center' }} onClick={() => setFound(0)}>
          Hiç yok (0)
        </button>
        {found !== null && found > 0 && (
          <div className="banner warn" role="alert">
            <b className="big">Bulduğunuz {found} adedi GÖZE GERİ BIRAKIN</b>
          </div>
        )}
        <div className="field">
          <label>Neden</label>
          <div className="row">
            {SHORT_REASONS.map((r) => (
              <button key={r} className={`chip select walk-tap${reason === r ? ' on' : ''}`} onClick={() => setPicked(r)}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Bu duraktaki rezervasyonlar bırakılır, göz sayım için kilitlenir ve siparişler vardiya sorumlusunun kararına kadar bekler. Göz sayılmadan önce
          “Eksik bildirimini geri al” ile geri alabilirsiniz.
        </p>
      </div>
    </Modal>
  );
}

/** While walking: the totes of orders cancelled meanwhile (or held, their marketplace no longer knowing them), to empty onto the return shelf. */
function CancelledStrip({ orders }: { orders: readonly RouteLeftOrder[] }) {
  return (
    <div className="banner bad walk-strip" role="alert">
      <Icon name="alert" />
      <div className="stack" style={{ gap: 4 }}>
        {orders.map((o) => (
          <div key={o.orderId}>
            <b>
              {tote(o.toteSlot)} · {o.status === 'on_hold' ? 'BEKLEMEDE' : 'İPTAL'}: iade rafına bırakın
            </b>
            <div className="walk-strip-sub">
              <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} short />{' '}
              {o.status === 'on_hold' ? 'pazaryerinde bulunamadı' : 'iptal edildi'} · sepette {o.units} ürün
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A tote of an order that left the route: not for the packing table. */
function LeftOrderStrip({ order }: { order: RouteLeftOrder }) {
  const held = order.status === 'on_hold';
  // held because its marketplace no longer knows it: its units go back to their bins, as a cancelled order's
  const back = !held || order.channelUnknown === true;
  const what = order.units === 0 ? 'sepette ürün yok' : back ? 'iade rafına bırakın' : 'ürünleri Sorunlu rafına koyun';
  return (
    <div className="banner bad walk-strip" role="alert">
      <Icon name="alert" />
      <div>
        <b>
          {tote(order.toteSlot)} · {held ? 'BEKLEMEDE' : 'İPTAL'}: {what}
        </b>
        <div className="walk-strip-sub">
          <span className="mono">{order.orderNumber}</span> <ChannelBadge channel={order.channel} short />
          {held ? (order.channelUnknown ? ' pazaryerinde bulunamadı, beklemede' : ' eksik nedeniyle beklemede') : ' iptal edildi'}
          {order.units > 0 && ` · sepette ${order.units} ürün`}
          {order.labelPrintedAt && ' · ürünlere yapıştırdığınız kargo etiketini sökün'}
        </div>
      </div>
    </div>
  );
}

/** The order's label was printed before the walk and is with the picker. */
function labelledAtBin(route: PickRoute, orderId: string): boolean {
  return route.asIs.some((o) => o.orderId === orderId && o.labelPrintedAt !== null && !o.packed);
}

/** The orders whose label taken along became void: their carrier changed after it printed (acil iş, 0069). */
const voidedLabels = (route: PickRoute, orderIds: readonly string[]) =>
  route.asIs.filter((o) => o.labelVoidAt !== undefined && !o.packed && orderIds.includes(o.orderId));

/**
 * At a stop of an order whose label in hand became void: not stuck on (taken off if it was), the goods go
 * to a desk with the order's tote, which prints the new label.
 */
function VoidLabel({ route, orderIds }: { route: PickRoute; orderIds: readonly string[] }) {
  const voided = voidedLabels(route, orderIds);
  if (voided.length === 0) return null;
  return (
    <div className="banner bad void-label" role="alert">
      <Icon name="alert" />
      <div>
        <div className="big">{VOID_LABEL_TEXT}</div>
        <div className="walk-strip-sub">
          {voided.map((o, i) => (
            <Fragment key={o.orderId}>
              {i > 0 && ', '}
              <span className="mono nowrap">{o.orderNumber}</span> <ChannelBadge channel={o.channel} short />
            </Fragment>
          ))}{' '}
          · ürünü sepetle paketleme masasına götürün
        </div>
      </div>
    </div>
  );
}

/** Why a label that should go along did not print: the order's label then prints at the desk. */
const SKIPPED: Readonly<Record<LabelSkip, string>> = {
  documents: 'belge koliye girecek ya da bekleniyor: masada basılır',
  no_desk: 'paketleme masası tanımlı değil',
  parcels_fixed: 'etiketi pazaryeri verdi: masada basılır',
  not_ready: 'etiket henüz hazır değil',
  not_in_hand: 'etiket elde değil: toplanınca masada basılır',
  carrier_changed: 'kargo değişti: elinizdeki etiketi yapıştırmayın, yeni etiket masada',
};

/** This device's printer, as the packing desk's "Etiketi bas" prints the order's stored label. */
const thisDevice: HerePrinter = {
  label: (orderId) => apiBlob(`/api/orders/${orderId}/documents/shipping_label`),
  zpl: (zpl) => printZpl(zpl, 'Kargo etiketleri'),
  pdf: printPdf,
};

/**
 * Before the walk: the labels of goods that take theirs along, printed at the desk where picking
 * starts when the route was made, each with its print state; one not printed is printed from here.
 * "Bu cihazdan bas" prints them on this device instead (the lead when the desk's printer computer is
 * off) and, once the picker says they came out, the desk does not print them. Once the picker left,
 * a desk label would wait behind them: the card goes, unless the desk is off and labels are missing.
 * It stands right above the stop and stays short (what to do, its buttons, the desk's print state;
 * the orders folded under "Siparişler"), so the stop's scan field mostly fits under it; when the two
 * do not fit on the screen, the card is the one in view (the scan field's revealAbove).
 */
function LabelsAhead({ route, onPrinted }: { route: PickRoute; onPrinted: (route: PickRoute) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // printed on this device: the desk forgets them only once the picker says they came out
  const [printed, setPrinted] = useState<readonly AsIsOrder[] | null>(null);
  // the orders, one line each: folded until asked for (the card keeps its height while the walk goes on)
  const [open, setOpen] = useState(false);
  const mode = labelsCard(route);
  const ahead = mode ? labelsAhead(route) : [];
  // the desk's print counts for these (it may wait while its computer is off)
  const atDesk = ahead.filter((o) => o.labelPrintedAt !== null && !o.labelPrintedHere);
  const states = usePrintStates(atDesk.map((o) => o.orderId));
  if (!mode) return null;
  const here = printableHere(route);
  // neither printed here nor out of the desk's printer (one the desk printed is in hand, its computer off or not)
  const missing = here.filter(labelMissing);
  const deskDown = !route.labelDeskOnline;
  const retry =
    mode === 'before' ? ahead.filter((o) => o.labelPrintedAt === null && (o.labelSkipped === null || o.labelSkipped === 'not_ready' || o.labelSkipped === 'no_desk')) : [];
  const hereLabels = labelCount(ahead.filter((o) => o.labelPrintedHere));
  const down = deskDown && missing.length > 0;
  const deskText = deskDownText(route.labelDesk, labelCount(missing));
  // "Bu cihazdan bas" for all that are missing: the card's lead while the desk is off, else under the orders
  const printAll = (lead: boolean) => (
    <button className={`btn sm${lead ? ' primary' : ''}`} disabled={busy} onClick={() => void printHere(missing)}>
      <Icon name="printer" size={16} /> {lead ? 'Bu cihazdan bas' : `${missing.length > 1 ? 'Hepsini bu cihazdan bas' : 'Bu cihazdan bas'} (${labelCount(missing)} etiket)`}
    </button>
  );
  // the desk's print of each order's label, as the desk's lists show it
  const jobOf = (orderId: string) => {
    const jobs = states.get(orderId);
    return jobs?.find((j) => j.kind === 'shipping_label') ?? jobs?.[0];
  };
  // the desk's print at a glance, from the route itself (the card does not grow when the states arrive);
  // the states only tell a failed print from a waiting one
  const notOut = atDesk.filter((o) => !o.labelOut);
  const failed = notOut.filter((o) => jobOf(o.orderId)?.state === 'failed');

  const print = async (body: { again?: boolean; orderIds?: string[] } | undefined) => {
    setBusy(true);
    try {
      const next = await api<PickRoute>('POST', `/api/wms/pick-routes/${route.id}/labels`, body);
      onPrinted(next);
      const left = next.asIs.filter((o) => o.labelAhead && !o.packed && o.labelPrintedAt === null && o.labelSkipped === 'not_ready').length;
      toast(left > 0 ? `${left} siparişin etiketi hâlâ hazır değil` : 'Etiketler masanın yazıcısına gönderildi', left > 0 ? 'bad' : 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const printHere = async (orders: readonly AsIsOrder[]) => {
    setBusy(true);
    try {
      const result = await printLabelsHere(orders, thisDevice);
      if (result.failed.length > 0) toast(result.failed.join(' · '), 'bad');
      if (result.printed.length > 0) setPrinted(result.printed);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  // the print dialog says nothing of the paper: only the picker knows the labels came out
  const cameOut = async (orders: readonly AsIsOrder[]) => {
    setBusy(true);
    try {
      const { route: next, refused } = await api<LabelsPrintedHere>('POST', `/api/wms/pick-routes/${route.id}/labels/printed-here`, {
        orderIds: orders.map((o) => o.orderId),
      });
      onPrinted(next);
      setPrinted(null);
      // the desk's waiting labels were withdrawn: its warning and lists catch up at once
      printsChanged();
      if (refused.length > 0) toast(refused.map((r) => `${r.orderNumber}: ${r.reason}`).join(' · '), 'bad');
      else toast(printedHereDone(labelCount(orders), route.labelDesk), 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`banner ${down ? 'bad' : retry.length > 0 || failed.length > 0 ? 'warn' : 'info-soft'} labels-ahead`} aria-label="Kargo etiketleri">
      <Icon name="printer" />
      <div className="labels-ahead-body">
        <div className="labels-ahead-title">
          {down
            ? deskText.title
            : atDesk.length > 0
              ? `Yürümeden önce ${labelCount(atDesk)} kargo etiketini ${route.labelDesk ? `${route.labelDesk} masasının` : 'masanın'} yazıcısından alın`
              : hereLabels > 0
                ? `${hereLabels} kargo etiketi bu cihazdan basıldı: yürümeden önce yanınıza alın`
                : 'Etiketler basılamadı'}
        </div>
        <div className="labels-ahead-row">
          {down && printAll(true)}
          {retry.length > 0 && (
            <button className="btn sm" disabled={busy} onClick={() => void print(undefined)}>
              <Icon name="printer" size={16} /> Etiketleri masada bas
            </button>
          )}
          <button className="btn sm ghost" aria-expanded={open} onClick={() => setOpen(!open)} title={open ? 'Siparişleri gizler' : 'Siparişleri ve etiketlerinin durumunu gösterir'}>
            <Icon name="list" size={16} /> {open ? 'Gizle' : `${ahead.length} sipariş`}
          </button>
          {/* the desk's print at a glance (while the desk is off the title says it all); the orders' own lines say which */}
          {atDesk.length > 0 &&
            !down &&
            (failed.length > 0 ? (
              <span className="chip bad">
                <Icon name="alert" size={14} /> {labelCount(failed)} etiket basılamadı
              </span>
            ) : notOut.length > 0 ? (
              <span className="chip warn">
                <Icon name="clock" size={14} /> {labelCount(notOut)} etiket basılıyor
              </span>
            ) : (
              <span className="chip ok">
                <Icon name="check" size={14} /> Masada basıldı
              </span>
            ))}
        </div>
        {open && down && deskText.hint && <p className="labels-ahead-hint">{deskText.hint}</p>}
        {open && (
          <ul className="labels-ahead-list">
            {ahead.map((o) => (
              <li key={o.orderId}>
                <span className="mono">{o.orderNumber}</span>
                <ChannelBadge channel={o.channel} short />
                {o.labelPrintedHere && o.labelPrintedAt ? (
                  <>
                    <span className="muted"> · {o.parcels} etiket </span>
                    <span className="chip ok">
                      <Icon name="check" size={14} /> Bu cihazdan basıldı {printedAt(o.labelPrintedAt)}
                    </span>
                  </>
                ) : o.labelPrintedAt ? (
                  <>
                    <span className="muted"> · {o.parcels} etiket </span>
                    <LabelStateCell states={states.get(o.orderId)} />
                    {mode === 'before' && !deskDown && (
                      <button
                        className="btn sm ghost"
                        disabled={busy}
                        onClick={() => void print({ again: true, orderIds: [o.orderId] })}
                        title="Sıkışan ya da çıkmayan etiketi masada yeniden basar"
                      >
                        Yeniden bas
                      </button>
                    )}
                  </>
                ) : (
                  <span className="muted"> · {o.labelSkipped ? SKIPPED[o.labelSkipped] : 'basılmadı'}</span>
                )}
                {/* the order the card's button prints alone needs no button of its own; for one in hand it is a plain reprint */}
                {here.includes(o) && !(missing.length === 1 && missing[0] === o) && (
                  <button className="btn sm ghost" disabled={busy} onClick={() => void printHere([o])} title="Bu siparişin etiketini bu cihazın yazıcısından basar">
                    Bu cihazdan bas
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {open && !down && missing.length > 0 && <div className="labels-ahead-row">{printAll(false)}</div>}
      </div>
      {printed && <PrintedHereDialog printed={printed} desk={route.labelDesk} busy={busy} onNo={() => setPrinted(null)} onYes={() => void cameOut(printed)} />}
    </section>
  );
}

/**
 * "Etiketler yazıcıdan çıktı mı?" after "Bu cihazdan bas": "Evet, etiketler elimde" and the desk does
 * not print them; "Hayır, çıkmadı" changes nothing (print again from here, or the desk prints them).
 */
function PrintedHereDialog(props: { printed: readonly AsIsOrder[]; desk: string | null; busy: boolean; onNo: () => void; onYes: () => void }) {
  const { printed, desk, busy, onNo, onYes } = props;
  const text = printedHereText(labelCount(printed), desk);
  return (
    <Modal
      title={PRINTED_HERE_QUESTION.title}
      onClose={onNo}
      footer={
        <>
          <button className="btn walk-tap" disabled={busy} onClick={onNo}>
            {PRINTED_HERE_QUESTION.no}
          </button>
          <button className="btn primary walk-tap" disabled={busy} onClick={onYes}>
            <Icon name="check" /> {PRINTED_HERE_QUESTION.yes}
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          <b>{text.sent}</b> <span className="muted">Siparişler:</span>{' '}
          {/* an order number stays on one line; the list breaks between them */}
          {printed.map((o, i) => (
            <Fragment key={o.orderId}>
              {i > 0 && ' '}
              <span className="nowrap">
                <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} short />
                {i < printed.length - 1 ? ',' : ''}
              </span>
            </Fragment>
          ))}
        </p>
        <p style={{ margin: 0 }}>{text.yes}</p>
        <p className="muted" style={{ margin: 0 }}>
          {text.no}
        </p>
      </div>
    </Modal>
  );
}

/**
 * At a bin of goods that take their label along: scan first, then the label on, and where the goods
 * go (dispatch when this stop finishes the order). "Etiket elimde yok": its label prints at the desk.
 * Short whatever the orders (where the goods go said once, the orders on one line, one "Etiket elimde
 * yok" that asks which order when there are more), so on a phone the labels card above and the scan
 * field under it fit the screen with it.
 */
function LabelAtBin({ task, route, onChanged }: { task: PickingRouteTask; route: PickRoute; onChanged: (route: PickRoute) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // "Etiket elimde yok" with more than one order: which one
  const [asking, setAsking] = useState(false);
  const labelled = task.distribution.filter((d) => labelledAtBin(route, d.orderId));
  if (labelled.length === 0) return null;
  // another bin of the same order is still to come: its goods wait with the picker until then
  const more = (orderId: string) => route.tasks.some((t) => t.sequence !== task.sequence && t.status === 'pending' && t.distribution.some((d) => d.orderId === orderId));
  const later = labelled.filter((d) => more(d.orderId)).length;
  // some go to dispatch now, some once their order's other goods are picked: said per order
  const mixed = later > 0 && later < labelled.length;
  const notInHand = async (orderId: string) => {
    setBusy(true);
    try {
      onChanged(await api<PickRoute>('POST', `/api/wms/pick-routes/${route.id}/labels/missing`, { orderId }));
      setAsking(false);
      toast('Etiket toplanınca masanın yazıcısından çıkar', 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const missingTitle = 'Etiket yanınızda değilse: ürün toplanınca etiketi masada basılır';
  return (
    <div className="banner info label-at-bin">
      <Icon name="tag" />
      <div className="label-at-bin-body">
        <div className="big">{labelled.some((d) => d.quantity > 1) ? 'Ürünü okutun, her birine bir etiket yapıştırın' : 'Ürünü okutun, sonra etiketini yapıştırın'}</div>
        <div>
          {later === labelled.length ? 'Siparişin diğer ürünleri de toplanınca hepsini Sevkiyat alanına bırakın.' : 'Sepete koymayın, Sevkiyat alanına bırakın.'}
        </div>
        <div className="label-at-bin-row">
          {labelled.map((d) => (
            <span key={d.allocationId}>
              <span className="mono nowrap">
                {d.orderNumber}
                {d.quantity > 1 ? ` × ${d.quantity}` : ''}
              </span>{' '}
              <ChannelBadge channel={d.channel} short />
              {mixed && more(d.orderId) && <span className="muted"> · diğer ürünleri de toplanınca</span>}
            </span>
          ))}
          {labelled.length === 1 ? (
            <button className="btn sm ghost" disabled={busy} onClick={() => void notInHand(labelled[0]!.orderId)} title={missingTitle}>
              Etiket elimde yok
            </button>
          ) : (
            <button className="btn sm ghost" aria-expanded={asking} onClick={() => setAsking(!asking)} title={missingTitle}>
              {asking ? 'Vazgeç' : 'Etiket elimde yok'}
            </button>
          )}
        </div>
        {asking && labelled.length > 1 && (
          <div className="label-at-bin-row">
            <span className="muted">Hangi siparişin etiketi yanınızda değil?</span>
            {labelled.map((d) => (
              <button key={d.allocationId} className="btn sm" disabled={busy} onClick={() => void notInHand(d.orderId)}>
                <span className="mono">{d.orderNumber}</span>
                <ChannelBadge channel={d.channel} short />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Finished({ route, onUndo, onUndoShort }: { route: PickRoute; onUndo: (() => void) | null; onUndoShort: (() => void) | null }) {
  const shorts = route.tasks.filter((t) => t.status === 'short').length;
  // which order went into which tote
  const slots = new Map<string, number | null>();
  for (const t of route.tasks) for (const d of t.distribution) if (!slots.has(d.orderNumber)) slots.set(d.orderNumber, d.toteSlot);
  for (const o of route.leftOrders) if (!slots.has(o.orderNumber)) slots.set(o.orderNumber, o.toteSlot);
  // orders of own-box goods were packed as picked: they go to dispatch, not to the packing table
  const asIs = new Map(route.asIs.map((o) => [o.orderNumber, o]));
  const left = new Map(route.leftOrders.map((o) => [o.orderNumber, o]));
  const channels = channelsOf(route);
  const shipped = route.asIs.filter((o) => o.packed);
  // labelled at the bin: the picker had the label; the others' labels came out of the desk's printer
  const labelled = shipped.filter((o) => o.labelAhead && o.labelPrintedAt !== null);
  // labelled, but packing waits (a document): the goods are at dispatch, the desk finishes it
  const unfinished = route.asIs.filter((o) => !o.packed && o.labelPrintedAt !== null);
  const toTables = [...slots.keys()].filter((n) => !asIs.get(n)?.packed && !asIs.get(n)?.labelPrintedAt && !left.has(n)).length;
  // the next step leads; what goes where (red totes, own-box goods, totes) under it
  const steps: string[] = [];
  if (left.size > 0) steps.push('Kırmızı sepetleri ayırın: paketlemeye gitmez, ne yapılacağı aşağıda yazıyor.');
  if (labelled.length + unfinished.length > 0) steps.push('Etiketlediğiniz ürünleri Sevkiyat alanına bırakın.');
  if (shipped.length > labelled.length) steps.push('Paketlemeden gidenlerin etiketini masanın yazıcısından alıp ürüne yapıştırın, Sevkiyat rafına bırakın.');
  if (toTables > 0) steps.push(route.mode === 'batch' ? 'Sepetleri paketleme masasına götürün.' : 'Sepeti paketleme masasına götürün.', 'Paketlemeye geçin.');
  else if (shipped.length > 0) steps.push('Sevkiyata geçin.');
  else steps.push('Paketlemeye giden sipariş yok: yeni rota oluşturun.');
  const go: StepLink =
    toTables > 0
      ? { label: 'Paketlemeye geç', to: '/paketleme', icon: 'box' }
      : shipped.length > 0
        ? { label: 'Sevkiyata geç', to: '/sevkiyat', icon: 'truck' }
        : { label: 'Yeni rota', to: '/toplama', icon: 'route' };
  const details = route.leftOrders.length > 0 || route.asIs.length > 0 || route.mode === 'batch';
  return (
    <div className="stack">
      <NextStep title="Rota bitti" tone={left.size > 0 ? 'warn' : 'ok'} steps={steps} go={go}>
        {shorts > 0 && (
          <p className="muted" style={{ margin: 0 }}>
            {shorts} durak eksik bildirildi; o siparişler sorunlu listesinde bekler.
          </p>
        )}
        <div className="row walk-done-actions">
          {go.to !== '/toplama' && (
            <button className="btn" onClick={() => navigate('/toplama')}>
              <Icon name="route" /> Yeni rota
            </button>
          )}
          {onUndo && (
            <button className="btn" onClick={onUndo}>
              <Icon name="undo" /> Son işlemi geri al
            </button>
          )}
          {onUndoShort && (
            <button className="btn" onClick={onUndoShort}>
              <Icon name="undo" /> Eksik bildirimini geri al
            </button>
          )}
        </div>
      </NextStep>
      {details && (
        <section className="card">
          <div className="card-b stack">
            {route.leftOrders.map((o) => (
              <LeftOrderStrip key={o.orderId} order={o} />
            ))}
            {route.asIs.length > 0 && (
              <div className="stack" style={{ gap: 6 }}>
                {route.asIs.map((o) => (
                  <div key={o.orderId} className={`banner ${o.packed ? 'info-soft' : 'warn'}`} style={{ padding: '10px 12px' }}>
                    <Icon name={o.packed ? 'printer' : 'box'} />
                    <div>
                      <b className="mono">{o.orderNumber}</b> <ChannelBadge channel={o.channel} short />
                      {o.toteSlot !== null && <span className="muted"> · Sepet {o.toteSlot}</span>}
                      <div style={{ fontWeight: 500 }}>
                        {!o.packed && o.labelVoidAt
                          ? 'Kargo değişti: elinizdeki etiketi yapıştırmayın (yapıştırdıysanız sökün). Ürünü paketleme masasına götürün; yeni etiket masada basılır.'
                          : o.packed && o.labelAhead && o.labelPrintedAt
                          ? `Etiketlendi${o.units > 1 ? ` (${o.units} koli)` : ''}: Sevkiyat alanına bırakın.`
                          : o.labelPrintedAt
                            ? 'Etiketlendi ama paketleme bekliyor (belge): ürünü Sevkiyat alanına bırakın; paketleme masasında “Tamamla” ile bitirilir.'
                            : o.packed
                            ? `Paketlemeden gider. ${o.parcels > 1 ? `${o.parcels} etiketi` : 'Etiketi'} ${o.stationCode ? `Masa ${o.stationCode}` : 'masanın'} yazıcısından alın, ürüne yapıştırıp sevkiyata bırakın.`
                            : 'Paketlemeden gider ama etiketi henüz hazır değil: paketleme masasında “Etiketi bas, tamamla” ile bitirin.'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {route.mode === 'batch' && (
              <div className="slots">
                {[...slots.entries()]
                  .filter(([order]) => !asIs.get(order)?.labelPrintedAt)
                  .sort((a, b) => (a[1] ?? 0) - (b[1] ?? 0))
                  .map(([order, slot]) => (
                    <div key={order} className={`slot${left.has(order) ? ' left' : ''}`}>
                      <small>
                        Sepet{channels.has(order) && <> <ChannelBadge channel={channels.get(order)!} short /></>}
                      </small>
                      <b>{slot}</b>
                      <span>
                        {order}
                        {left.has(order) && (left.get(order)!.status === 'on_hold' ? ' · beklemede' : ' · iptal')}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
